import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail, resolveImageUrl } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { fireConfetti } from "../../components/Confetti";
import { Lock, Check, X, Gift } from "lucide-react";

export default function Trophaeen() {
  const { user, refresh } = useAuth();
  const [geschenke, setGeschenke] = useState([]);
  const [selected, setSelected] = useState(null);
  const [adresse, setAdresse] = useState(user.anlagenadresse || "");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/geschenke").then((r) => setGeschenke(r.data));
  useEffect(() => { load(); }, []);

  const einlosen = async () => {
    setError(""); setLoading(true);
    try {
      await api.post("/geschenke/einlosen", { geschenk_id: selected.id, lieferadresse: adresse });
      fireConfetti("strong");
      setSelected(null);
      await load();
      await refresh();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="trophaeen-page">
      <div>
        <h1 className="text-3xl font-heading font-bold text-falke-blue">Trophäen-Regal</h1>
        <p className="text-falke-anthracite/70 mt-1">Tausche deine FalkeCoins gegen Premium-Geschenke.</p>
        <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-falke-blue/[0.06] text-falke-blue text-sm">
          Verfügbar: <span className="tabular font-bold text-falke-orange">{user.verfuegbare_coins} FC</span>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {geschenke.map((g) => (
          <button
            key={g.id}
            onClick={() => g.erreichbar && setSelected(g)}
            data-testid={`geschenk-${g.id}`}
            className={`relative text-left falke-card overflow-hidden falke-card-hover transition-all ${
              g.eingeloest ? "ring-2 ring-falke-success" : ""
            } ${!g.stufe_erreicht ? "opacity-40 grayscale cursor-not-allowed" : ""}
              ${g.stufe_erreicht && !g.erreichbar ? "opacity-70" : ""}`}
          >
            {g.bild_url ? (
              <img src={g.bild_url} alt={g.name} className="w-full aspect-square object-cover" />
            ) : (
              <div className="w-full aspect-square bg-falke-blue/10" />
            )}
            <div className="p-3">
              <div className="font-medium text-sm text-falke-anthracite line-clamp-2">{g.name}</div>
              <div className="flex items-center justify-between mt-2">
                <div className="font-heading text-falke-orange tabular font-bold">{g.coin_preis} FC</div>
                <div className="text-[10px] uppercase tracking-wider text-falke-blue/60">{g.mindest_stufe}</div>
              </div>
            </div>
            {g.eingeloest && (
              <div className="absolute top-2 left-2 px-2 py-1 rounded-md bg-falke-success text-white text-[10px] font-bold uppercase flex items-center gap-1">
                <Check size={12} /> Eingelöst
              </div>
            )}
            {!g.eingeloest && g.erreichbar && (
              <div className="absolute top-2 left-2 px-2 py-1 rounded-md bg-falke-orange text-white text-[10px] font-bold uppercase">
                Erreichbar
              </div>
            )}
            {!g.stufe_erreicht && (
              <div className="absolute top-2 left-2 px-2 py-1 rounded-md bg-falke-blue/80 text-white text-[10px] font-bold flex items-center gap-1">
                <Lock size={12} /> {g.mindest_stufe}
              </div>
            )}
          </button>
        ))}
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-falke-blue/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
            data-testid="einloesung-modal"
          >
            <motion.div
              initial={{ y: 100 }} animate={{ y: 0 }} exit={{ y: 100 }}
              className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-start justify-between gap-3 mb-4">
                <h3 className="font-heading text-2xl text-falke-blue">{selected.name}</h3>
                <button onClick={() => setSelected(null)} className="text-falke-blue/60 hover:text-falke-blue" data-testid="modal-close"><X /></button>
              </div>
              {selected.bild_url && <img src={resolveImageUrl(selected.bild_url)} alt="" className="w-full h-40 object-cover rounded-xl mb-3" />}
              <p className="text-sm text-falke-anthracite/80 mb-4">{selected.beschreibung}</p>

              <label className="block text-sm font-medium text-falke-blue mb-1.5">Lieferadresse</label>
              <textarea
                rows={2}
                value={adresse}
                onChange={(e) => setAdresse(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-falke-blue/15 text-sm"
                data-testid="einloesung-adresse"
              />

              <div className="mt-5 p-4 rounded-xl bg-gradient-to-r from-falke-orange/15 to-falke-gold/15 border border-falke-orange/30 flex items-center justify-between">
                <div>
                  <div className="text-xs text-falke-blue/70">Du bezahlst</div>
                  <div className="font-heading text-2xl font-bold text-falke-orange tabular">{selected.coin_preis} FC</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-falke-blue/70">Verbleiben</div>
                  <div className="font-heading text-xl text-falke-blue tabular">{user.verfuegbare_coins - selected.coin_preis} FC</div>
                </div>
              </div>

              {error && <div className="text-sm text-red-600 mt-3">{error}</div>}

              <button onClick={einlosen} disabled={loading || !adresse} className="btn-primary w-full mt-5" data-testid="einloesen-confirm">
                {loading ? "Löse ein…" : `${selected.coin_preis} FC einlösen`}
              </button>
              <p className="text-xs text-falke-anthracite/55 mt-3 text-center">Mit der Einlösung werden FalkeCoins von deinem Konto abgezogen.</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
